import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { AuthLegalFooter, LegalModal } from '../components/AuthLegalFooter';
import WasslaLogo from '../components/WasslaLogo';
import ResetPasswordModal from '../components/ResetPasswordModal';
import BackButton from '../components/BackButton';
import { toAbsoluteUrl } from '../lib/media';
import { getApiBase } from '../api/httpClient';
import './Auth.css';

function Auth() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [mode, setMode] = useState(searchParams.get('mode') || 'login');
  const [formData, setFormData] = useState({ email: '', password: '', name: '', avatar: '' });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [shakeFields, setShakeFields] = useState(false);
  const [cooldown, setCooldown] = useState(0); // seconds remaining
  
  // Legal modal state
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalType, setLegalModalType] = useState('terms'); // 'terms' or 'privacy'

  // Forgot-password reset modal
  const [resetModalOpen, setResetModalOpen] = useState(false);
  
  // Terms agreement checkbox (required for signup)
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Get referral code from URL
  const referralCode = searchParams.get('ref') || '';
  const isRTL = i18n.language === 'ar';

  // Add global error handlers for debugging
  useEffect(() => {
    const handleGlobalError = (e) => {
      console.error('[GlobalError]:', e.error || e.message);
    };
    const handleUnhandledRejection = (e) => {
      console.error('[UnhandledRejection]:', e.reason);
    };
    
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Live cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Derived validation states
  const isPasswordMatch = formData.password && confirmPassword && formData.password === confirmPassword;
  const showPasswordMismatchError = mode === 'signup' && confirmPassword && !isPasswordMatch;
  
  // Form validation for signup (includes terms agreement)
  const isSignupFormValid = mode === 'signup' 
    ? formData.name && formData.email && formData.password && isPasswordMatch && agreedToTerms
    : formData.email && formData.password;

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingAvatar(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('files', file);

      // iter282 — use getApiBase() so live custom domains hit
      // same-origin /api/upload; preview / localhost keep the
      // build-time absolute URL.
      const response = await fetch(`${getApiBase()}/api/upload`, {
        method: 'POST',
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        const avatarUrl = data.images[0].startsWith('/uploads/') 
          ? `${data.images[0]}?v=${Date.now()}` 
          : data.images[0];
        setFormData({ ...formData, avatar: avatarUrl });
      } else {
        toast.error(t('errUploadFailed'));
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(t('errNetwork'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const validateFields = () => {
    const errs = {};
    const emailVal = formData.email.trim();
    if (!emailVal) {
      errs.email = t('authEmailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      errs.email = t('authEmailInvalid');
    }
    if (!formData.password) {
      errs.password = t('authPasswordRequired');
    }
    if (mode === 'signup' && !formData.name?.trim()) {
      errs.name = t('authNameRequired');
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (cooldown > 0) return; // blocked by countdown

    if (!validateFields()) return;

    // Additional validation for signup
    if (mode === 'signup' && !isPasswordMatch) {
      setError(t('passwordsDoNotMatch'));
      return;
    }

    setLoading(true);

    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const payload = mode === 'login' 
        ? { email: formData.email, password: formData.password }
        : { 
            email: formData.email, 
            password: formData.password, 
            name: formData.name, 
            avatar: formData.avatar 
          };
      
      // iter299 — Use `getApiBase()` (same-origin on custom domains like
      // wassla.tech, env URL on preview/dev) so login/signup survives
      // custom-domain deploys without rebuilding the frontend.
      const url = mode === 'signup' && referralCode 
        ? `${getApiBase()}${endpoint}?referral_code=${referralCode}`
        : `${getApiBase()}${endpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const responseStatus = response.status;
      let responseData;
      try {
        responseData = await response.clone().json();
      } catch (e) {
        try {
          const text = await response.clone().text();
          responseData = { detail: text };
        } catch (_) {
          responseData = { detail: '' };
        }
      }

      if (responseStatus !== 200) {
        // Handle rate-limit / cooldown (429)
        if (responseStatus === 429) {
          const detail = responseData?.detail || {};
          const secs = detail.retryAfter || detail.retry_after || 30;
          console.log('[Auth] 429 cooldown received, retryAfter:', secs);
          setCooldown(secs);
          setError(isRTL ? detail.message_ar || t('authCooldown') : detail.message || t('authCooldown'));
          return;
        }

        // Handle invalid credentials (401) — generic message
        if (responseStatus === 401) {
          setError(t('authLoginError'));
          setFormData(prev => ({ ...prev, password: '' }));
          setShakeFields(true);
          setTimeout(() => setShakeFields(false), 500);
          return;
        }
        
        // Handle account status errors (403)
        if (responseStatus === 403) {
          const detail = responseData?.detail || {};
          const code = detail.code || '';
          
          if (code === 'ACCOUNT_SUSPENDED') {
            navigate('/account-suspended', { 
              state: { suspendedUntil: detail.suspended_until, reason: detail.reason },
              replace: true 
            });
            return;
          }
          if (code === 'ACCOUNT_BANNED') {
            navigate('/account-banned', { 
              state: { reason: detail.reason, reasonCode: detail.reason_code, canAppeal: detail.can_appeal, email: formData.email },
              replace: true 
            });
            return;
          }
          if (code === 'ACCOUNT_DELETED') {
            setError(t('errAccountDeleted'));
            return;
          }
          setError(t('authLoginError'));
          return;
        }
        
        if (responseStatus === 409) {
          setError(t('errEmailExists'));
          return;
        }
        if (responseStatus >= 500) {
          setError(t('errServerError'));
          return;
        }
        
        setError(t('authLoginError'));
        return;
      }

      // Success
      const data = responseData;
      login(data.token, data.user, data.is_new_user, data.needs_onboarding);
      
      // Route based on onboarding status — backend is source of truth
      const onboardingDone = data.user?.onboarding_completed || data.user?.onboardingCompleted;
      const hasAccountType = !!(data.user?.account_type || data.user?.accountType || data.user?.profile_type);
      
      if (!onboardingDone && !hasAccountType) {
        // New user or incomplete onboarding — go to onboarding
        navigate('/onboarding', { replace: true });
      } else {
        // Existing completed user — go to intended destination or home
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
      }
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
        setError(t('errNoInternet'));
      } else {
        setError(t('errNetwork'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset confirm password and terms agreement when switching modes
  const handleModeSwitch = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError('');
    setFieldErrors({});
    setConfirmPassword('');
    setAgreedToTerms(false);
  };

  return (
    <div className="auth-page">
      {/* Back Button — iter270: shared RTL-aware BackButton. LTR shows
          a left-pointing chevron flushed to the left; RTL shows a
          right-pointing chevron flushed to the right (CSS reverses the
          row automatically because `dir="rtl"` is on <html>). The CSS
          rotate(180deg) on the legacy `.back-button svg` selector is
          NOT applied here — Lucide chevrons render correctly per the
          `isRTL` branch inside BackButton itself. */}
      <BackButton
        to="/welcome"
        className="auth-back-button"
        size={22}
        label={t('back')}
        testId="back-to-welcome"
      />

      <div className="auth-container">
        <div className="auth-card">
          {/* Wassla Logo - Direct Image with Inline Styles */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '1.5rem',
            padding: '0.5rem 0'
          }}>
            <WasslaLogo size="auth" />
          </div>
          
          <h2 className="auth-title">{mode === 'login' ? t('signIn') : t('signUp')}</h2>

          {/* Show referral bonus banner for signup */}
          {mode === 'signup' && referralCode && (
            <div className="referral-bonus-banner" data-testid="referral-bonus-banner">
              <span className="referral-bonus-icon">🎁</span>
              <span className="referral-bonus-text">
                {t('authReferralBonus')}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            {mode === 'signup' && (
              <>
                <div className="form-group">
                  <label className="form-label">{t('name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setFieldErrors(prev => ({ ...prev, name: undefined })); }}
                    className={`input ${fieldErrors.name ? 'input-error' : ''}`}
                    placeholder={t('name')}
                    data-testid="name-input"
                  />
                  {fieldErrors.name && <p className="field-error" data-testid="name-error">{fieldErrors.name}</p>}
                </div>

                <div className="form-group">
                  <label className="form-label">{t('profilePhoto')}</label>
                  <div className="avatar-upload">
                    {formData.avatar && (
                      <img src={toAbsoluteUrl(formData.avatar)} alt="Avatar" className="avatar-preview" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="file-input"
                      data-testid="avatar-upload"
                    />
                    {uploadingAvatar && <p className="upload-status">{t('uploading')}</p>}
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label className="form-label">{t('email')} *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setFieldErrors(prev => ({ ...prev, email: undefined })); }}
                className={`input ${fieldErrors.email || (error && shakeFields) ? 'input-error' : ''} ${error && shakeFields ? 'input-shake' : ''}`}
                placeholder={t('email')}
                data-testid="email-input"
              />
              {fieldErrors.email && <p className="field-error" data-testid="email-error">{fieldErrors.email}</p>}
            </div>

            <div className="form-group">
              <label className="form-label">{t('password')} *</label>
              <div className={`password-input-container ${error && shakeFields ? 'input-shake' : ''}`}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => { setFormData({ ...formData, password: e.target.value }); setFieldErrors(prev => ({ ...prev, password: undefined })); }}
                  className={`input ${fieldErrors.password || (error && shakeFields) ? 'input-error' : ''}`}
                  placeholder={t('password')}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle-btn"
                  aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                  data-testid="toggle-password"
                >
                  {showPassword ? (
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {fieldErrors.password && <p className="field-error" data-testid="password-error">{fieldErrors.password}</p>}
              {mode === 'login' && (
                <div className="forgot-password-row">
                  <button
                    type="button"
                    className="forgot-password-link"
                    data-testid="forgot-password-link"
                    onClick={(e) => { e.stopPropagation(); setResetModalOpen(true); }}
                  >
                    {t('forgotPassword')}
                  </button>
                </div>
              )}
            </div>

            {/* Confirm Password Field - Only shown for signup */}
            {mode === 'signup' && (
              <div className="form-group">
                <label className="form-label">{t('confirmPasswordLabel')} *</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`input ${showPasswordMismatchError ? 'input-error' : ''}`}
                    placeholder={t('confirmPasswordPlaceholder')}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="password-toggle-btn"
                    aria-label={showConfirmPassword ? t('hidePassword') : t('showPassword')}
                    data-testid="toggle-confirm-password"
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {showPasswordMismatchError && (
                  <p className="field-error" data-testid="password-mismatch-error">
                    {t('passwordsDoNotMatch')}
                  </p>
                )}
              </div>
            )}

            {/* Terms Agreement Checkbox - Only shown for signup */}
            {mode === 'signup' && (
              <div className="terms-agreement-wrapper">
                <label className={`terms-checkbox-label ${isRTL ? 'rtl' : ''}`} data-testid="terms-checkbox-label">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="terms-checkbox"
                    data-testid="terms-checkbox"
                  />
                  <span className="checkbox-custom"></span>
                  <span className="terms-text">
                    {isRTL ? (
                      <>
                        أوافق على{' '}
                        <button 
                          type="button" 
                          onClick={() => { setLegalModalType('terms'); setLegalModalOpen(true); }}
                          className="terms-link"
                        >
                          شروط الاستخدام
                        </button>
                        {' '}و{' '}
                        <button 
                          type="button" 
                          onClick={() => { setLegalModalType('privacy'); setLegalModalOpen(true); }}
                          className="terms-link"
                        >
                          سياسة الخصوصية
                        </button>
                      </>
                    ) : (
                      <>
                        I agree to the{' '}
                        <button 
                          type="button" 
                          onClick={() => { setLegalModalType('terms'); setLegalModalOpen(true); }}
                          className="terms-link"
                        >
                          Terms of Use
                        </button>
                        {' '}and{' '}
                        <button 
                          type="button" 
                          onClick={() => { setLegalModalType('privacy'); setLegalModalOpen(true); }}
                          className="terms-link"
                        >
                          Privacy Policy
                        </button>
                      </>
                    )}
                  </span>
                </label>
              </div>
            )}

            {error && <div className="error-message" data-testid="auth-error">{error}</div>}

            {cooldown > 0 && (
              <div className="cooldown-banner" data-testid="cooldown-banner">
                {isRTL
                  ? `حاول مرة أخرى بعد ${cooldown} ثانية`
                  : `Try again in ${cooldown}s`}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || cooldown > 0 || (mode === 'signup' && !isSignupFormValid)}
              className="btn btn-auth-submit w-full"
              data-testid="submit-button"
            >
              {cooldown > 0 ? (
                isRTL
                  ? `حاول مرة أخرى بعد ${cooldown} ثانية`
                  : `Try again in ${cooldown}s`
              ) : loading ? (
                <span className="auth-btn-loading">
                  <svg className="auth-spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                  {mode === 'login' ? t('signingIn') : t('loading')}
                </span>
              ) : (mode === 'login' ? t('loginBtn') : t('signupSubmit'))}
            </button>
          </form>

          {/* 
            ============================================================
            FUTURE: Add Google Sign-In Button Here
            ============================================================
            Example implementation:
            
            <div className="auth-divider">
              <span>{isRTL ? 'أو' : 'or'}</span>
            </div>
            
            <button className="btn-google" onClick={handleGoogleSignIn}>
              <img src="/google-icon.svg" alt="Google" />
              {t('gsii.continueWithGoogle')}
            </button>
            
            IMPORTANT: Legal footer below will remain visible regardless
            of how many OAuth providers are added above it.
            ============================================================
          */}

          {/* Legal Footer - MUST be visible below ALL auth methods */}
          <AuthLegalFooter 
            locale={i18n.language}
            onOpenPrivacy={() => {
              setLegalModalType('privacy');
              setLegalModalOpen(true);
            }}
            onOpenTerms={() => {
              setLegalModalType('terms');
              setLegalModalOpen(true);
            }}
          />

          <div className="auth-toggle">
            <p>
              {mode === 'login' ? t('dontHaveAccount') : t('alreadyHaveAccount')}
              <button
                onClick={handleModeSwitch}
                className="toggle-link"
                data-testid="toggle-auth-mode"
              >
                {mode === 'login' ? t('signUp') : t('signIn')}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Legal Modal (Terms & Privacy) */}
      <LegalModal
        open={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        type={legalModalType}
        locale={i18n.language}
      />

      {/* Forgot-password reset flow */}
      <ResetPasswordModal
        open={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        defaultEmail={formData.email}
      />
    </div>
  );
}

export default Auth;
